import { useI18n } from 'vue-i18n'
import { useFirestore } from 'vuefire'
import { useAppStore } from '@/stores/app'
import { logger } from '@/utils/logger'
import { useProjectsStore } from '@/stores/projects'
import { useTenantsStore } from '@/stores/tenants'
import { generateAuthoredDocumentPdf } from '@/utils/pdf/authoredDocument'
import { sanitizeFilename } from '@/utils/filename'
import {
  readAuthoredDocument,
  type AuthoredDocumentContentRow,
} from '@/composables/useAuthoredDocument'
import type { AuthoredDocumentExportKind } from '@/composables/authoredDocumentExportOptions'

export type { AuthoredDocumentExportKind } from '@/composables/authoredDocumentExportOptions'

/**
 * The three ways a document leaves the app (MYAC-1162).
 *
 * One place, because three surfaces offer them — the document page, the chat panel
 * and the library — and an export that behaves differently depending on where the
 * user clicked it is a bug waiting to be reported as "the PDF is different".
 *
 * The DOCUMENT itself is the caller's; the tenant and project names are ambient and
 * read here, so no surface has to plumb page furniture through to an export menu.
 */
export function useAuthoredDocumentExport() {
  const { t } = useI18n()
  const appStore = useAppStore()
  const projectsStore = useProjectsStore()
  const tenantsStore = useTenantsStore()

  const KNOWN_STATUSES = ['draft', 'final', 'archived']

  /** The same tolerant mapping the chip uses — a PDF must not print a raw enum either. */
  function statusLabel(state: string | undefined): string {
    const value = state ?? 'draft'
    return KNOWN_STATUSES.includes(value)
      ? t(`authoredDocuments.status.${value}`)
      : t('authoredDocuments.status.other')
  }

  /** `Project_doc-id_2026-09-08` — findable in a downloads folder a month later. */
  function baseFilename(row: AuthoredDocumentContentRow): string {
    const project = projectsStore.currentProject?.name ?? ''
    const date = new Date().toISOString().slice(0, 10)
    const parts = [
      project && sanitizeFilename(project),
      sanitizeFilename(row.doc_id || 'document'),
      date,
    ]
    return parts.filter(Boolean).join('_')
  }

  function exportPdf(row: AuthoredDocumentContentRow): void {
    try {
      generateAuthoredDocumentPdf(
        {
          title: row.title,
          markdown: row.markdown,
          statusLabel: statusLabel(row.lifecycle_state),
          revision: row.revision,
          tenantName: tenantsStore.currentTenant?.name ?? '',
          projectName: projectsStore.currentProject?.name ?? '',
          translate: t,
        },
        `${baseFilename(row)}.pdf`,
      )
    } catch (error) {
      // Generation runs entirely in the browser over LLM-authored content, so it can
      // fail on input nobody anticipated. Silent is the wrong answer: the user clicked
      // Download and nothing arrived, and they would keep clicking.
      logger.error('Authored document PDF export failed', error)
      appStore.toast(
        t('authoredDocuments.export.failed_title'),
        t('authoredDocuments.export.failed'),
        'error',
        5000,
      )
    }
  }

  /**
   * The RAW body, heading included: a standalone file has no header chrome, so the
   * `h1` the screen strips is the only title it carries.
   */
  function downloadMarkdown(row: AuthoredDocumentContentRow): void {
    const blob = new Blob([row.markdown], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${baseFilename(row)}.md`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  /** Same reasoning as the download: what is copied is the document, not the view. */
  async function copyMarkdown(row: AuthoredDocumentContentRow): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(row.markdown)
      return true
    } catch (error) {
      // A denied clipboard permission is the user's browser saying no, not a fault to
      // shout about — the caller decides what the user sees. It is still logged: a
      // copy that silently does nothing is exactly the report that arrives with no
      // detail attached.
      logger.error('Authored document clipboard copy failed', error)
      return false
    }
  }

  /**
   * The same three exports from a surface that holds only the document's id — the
   * library row (MYAC-1667), whose index row carries no body. The body is read once,
   * on the click, then handed to the very functions the reader calls, so a file from
   * the list and a file from the reader cannot differ.
   */
  async function exportDocument(
    projectId: string,
    docId: string,
    kind: AuthoredDocumentExportKind,
  ): Promise<void> {
    let failed = false
    const row = await readAuthoredDocument(useFirestore(), projectId, docId, {
      onError: () => (failed = true),
    })
    if (!row) {
      // Gone (deleted from the chat while the list still showed it) is not a failed
      // read: the reader's own two sentences, so each says what actually happened.
      appStore.toast(
        t('authoredDocuments.export.failed_title'),
        t(failed ? 'authoredDocuments.readFailed' : 'authoredDocuments.notFound'),
        'error',
        5000,
      )
      return
    }
    await runExport(row, kind)
  }

  /**
   * One export, chosen by name — what every menu calls. Copying says how it went:
   * it is invisible otherwise, the screen looks identical either way.
   */
  async function runExport(
    row: AuthoredDocumentContentRow,
    kind: AuthoredDocumentExportKind,
  ): Promise<void> {
    if (kind === 'pdf') return exportPdf(row)
    if (kind === 'markdown') return downloadMarkdown(row)
    const ok = await copyMarkdown(row)
    appStore.toast(
      t(ok ? 'authoredDocuments.export.copied' : 'authoredDocuments.export.copy_failed'),
      '',
      ok ? 'success' : 'warn',
      3000,
    )
  }

  return {
    exportPdf,
    downloadMarkdown,
    copyMarkdown,
    runExport,
    exportDocument,
    statusLabel,
    baseFilename,
  }
}
